import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, User, Trash2 } from 'lucide-react';
import { confirmDialog } from '../ui/confirm-dialog';

export const SuggestIdeaModal = ({ show, onClose, ideaForm, setIdeaForm, onSubmit }) => {
  if (!show) return null;
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
        <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 400, maxHeight: '85vh', overflow: 'auto' }}>
          <div style={{ padding: '20px 20px 16px', borderBottom: '1px solid #F3F4F6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ fontSize: 18, fontWeight: 600, color: '#1F2937' }}>Suggest an Idea</h3>
            <button onClick={onClose} data-testid="close-suggest-modal" style={{ background: 'none', border: 'none', fontSize: 24, color: '#9CA3AF', cursor: 'pointer' }}>x</button>
          </div>
          <div style={{ padding: 20 }}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Idea Title *</label>
              <input type="text" value={ideaForm.title} onChange={(e) => setIdeaForm(prev => ({ ...prev, title: e.target.value }))}
                placeholder="Give your idea a catchy title" data-testid="idea-title-input"
                style={{ width: '100%', padding: '12px 14px', border: '1px solid #E5E7EB', borderRadius: 10, fontSize: 14, outline: 'none' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Description *</label>
              <textarea value={ideaForm.description} onChange={(e) => setIdeaForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Describe your idea in detail" rows={3} data-testid="idea-description-input"
                style={{ width: '100%', padding: '12px 14px', border: '1px solid #E5E7EB', borderRadius: 10, fontSize: 14, outline: 'none', resize: 'vertical' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Location</label>
              <input type="text" value={ideaForm.location} onChange={(e) => setIdeaForm(prev => ({ ...prev, location: e.target.value }))}
                placeholder="Where would this take place?"
                style={{ width: '100%', padding: '12px 14px', border: '1px solid #E5E7EB', borderRadius: 10, fontSize: 14, outline: 'none' }} />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 6 }}>Event Outline / Agenda</label>
              <textarea value={ideaForm.agenda} onChange={(e) => setIdeaForm(prev => ({ ...prev, agenda: e.target.value }))}
                placeholder="Outline the key activities or agenda" rows={3}
                style={{ width: '100%', padding: '12px 14px', border: '1px solid #E5E7EB', borderRadius: 10, fontSize: 14, outline: 'none', resize: 'vertical' }} />
            </div>
            <button onClick={onSubmit} data-testid="submit-idea-btn"
              style={{ width: '100%', padding: '14px', background: '#5DBFAD', color: 'white', border: 'none', borderRadius: 12, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
              Submit Idea
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export const VoteIdeasModal = ({ show, onClose, submittedIdeas, setSubmittedIdeas, currentIdeaIndex, setCurrentIdeaIndex, selectedAdminEvent, user }) => {
  if (!show) return null;
  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
        <motion.div initial={{ scale: 0.95, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 380, overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', background: '#5DBFAD', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <h3 style={{ fontSize: 16, fontWeight: 600, color: 'white', margin: 0 }}>Vote on Ideas</h3>
              {selectedAdminEvent && <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', margin: '4px 0 0' }}>{selectedAdminEvent.title}</p>}
            </div>
            <button onClick={onClose} data-testid="close-vote-modal"
              style={{ background: 'rgba(255,255,255,0.2)', border: 'none', width: 28, height: 28, borderRadius: '50%', color: 'white', fontSize: 18, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              x
            </button>
          </div>
          <div style={{ padding: '20px', minHeight: 300 }}>
            {submittedIdeas.length > 0 ? (
              <>
                <AnimatePresence mode="wait">
                  <motion.div key={submittedIdeas[currentIdeaIndex]?.id}
                    initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
                    style={{ background: '#F8FAFB', borderRadius: 16, padding: 20, border: '1px solid #E5E7EB' }}>
                    <h4 style={{ fontSize: 17, fontWeight: 600, color: '#1F2937', marginBottom: 12 }}>{submittedIdeas[currentIdeaIndex]?.title}</h4>
                    {submittedIdeas[currentIdeaIndex]?.description && (
                      <div style={{ marginBottom: 12 }}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Description</p>
                        <p style={{ fontSize: 14, color: '#4B5563', lineHeight: 1.5, margin: 0 }}>{submittedIdeas[currentIdeaIndex]?.description}</p>
                      </div>
                    )}
                    {submittedIdeas[currentIdeaIndex]?.agenda && (
                      <div style={{ marginBottom: 12 }}>
                        <p style={{ fontSize: 12, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Agenda / Outline</p>
                        <p style={{ fontSize: 14, color: '#4B5563', lineHeight: 1.5, margin: 0 }}>{submittedIdeas[currentIdeaIndex]?.agenda}</p>
                      </div>
                    )}
                    {submittedIdeas[currentIdeaIndex]?.location && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6B7280', marginBottom: 12 }}>
                        <MapPin size={14} />{submittedIdeas[currentIdeaIndex]?.location}
                      </div>
                    )}
                    <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <User size={14} />Submitted by {submittedIdeas[currentIdeaIndex]?.submittedBy}
                      </div>
                      {submittedIdeas[currentIdeaIndex]?.userId === user?.id && (
                        <button onClick={async () => {
                          if (await confirmDialog({ title: 'Delete idea?', message: 'This will permanently remove your suggestion.', confirmText: 'Delete', danger: true })) {
                            const newIdeas = submittedIdeas.filter((_, i) => i !== currentIdeaIndex);
                            setSubmittedIdeas(newIdeas);
                            if (currentIdeaIndex >= newIdeas.length && newIdeas.length > 0) setCurrentIdeaIndex(newIdeas.length - 1);
                            if (newIdeas.length === 0) onClose();
                          }
                        }}
                          style={{ padding: '4px 8px', background: '#FEE2E2', color: '#DC2626', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Trash2 size={12} />Delete
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 16, borderTop: '1px solid #E5E7EB' }}>
                      <span style={{ fontSize: 14, color: '#6B7280' }}>{submittedIdeas[currentIdeaIndex]?.votes} votes</span>
                      <button onClick={() => {
                        setSubmittedIdeas(prev => prev.map((idea, i) =>
                          i === currentIdeaIndex ? { ...idea, votes: idea.hasVoted ? idea.votes - 1 : idea.votes + 1, hasVoted: !idea.hasVoted } : idea
                        ));
                      }}
                        data-testid="vote-idea-btn"
                        style={{ padding: '10px 24px', background: submittedIdeas[currentIdeaIndex]?.hasVoted ? '#F3F4F6' : '#F8AD7B', color: submittedIdeas[currentIdeaIndex]?.hasVoted ? '#6B7280' : 'white', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        {submittedIdeas[currentIdeaIndex]?.hasVoted ? 'Voted' : 'Vote'}
                      </button>
                    </div>
                  </motion.div>
                </AnimatePresence>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginTop: 20 }}>
                  <button onClick={() => setCurrentIdeaIndex(prev => Math.max(0, prev - 1))} disabled={currentIdeaIndex === 0}
                    style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid #E5E7EB', background: currentIdeaIndex === 0 ? '#F9FAFB' : 'white', color: currentIdeaIndex === 0 ? '#D1D5DB' : '#6B7280', cursor: currentIdeaIndex === 0 ? 'not-allowed' : 'pointer', fontSize: 16 }}>
                    &lsaquo;
                  </button>
                  <span style={{ fontSize: 13, color: '#9CA3AF' }}>{currentIdeaIndex + 1} of {submittedIdeas.length}</span>
                  <button onClick={() => setCurrentIdeaIndex(prev => Math.min(submittedIdeas.length - 1, prev + 1))} disabled={currentIdeaIndex >= submittedIdeas.length - 1}
                    style={{ width: 36, height: 36, borderRadius: '50%', border: '1px solid #E5E7EB', background: currentIdeaIndex >= submittedIdeas.length - 1 ? '#F9FAFB' : 'white', color: currentIdeaIndex >= submittedIdeas.length - 1 ? '#D1D5DB' : '#6B7280', cursor: currentIdeaIndex >= submittedIdeas.length - 1 ? 'not-allowed' : 'pointer', fontSize: 16 }}>
                    &rsaquo;
                  </button>
                </div>
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <p style={{ color: '#6B7280' }}>No ideas submitted yet. Be the first!</p>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};
